import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Dropdown, Form, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faSave,
  faTrash,
  faEdit,
  faCog,
  faGripVertical,
  faEllipsisV,
  faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { GridStack } from 'gridstack';
import 'gridstack/dist/gridstack.min.css';

import dashboardService from '../../../services/dashboardService';
import WidgetLibrary from './WidgetLibrary';
import WidgetRenderer from './WidgetRenderer';
import WidgetConfigModal from './WidgetConfigModal';
import Flex from '../../common/Flex';
import { useAuth } from '../../../contexts/AuthContext';
import './DashboardBuilder.css';

/**
 * Componente principal para construir dashboards personalizados con GridStack
 */
const DashboardBuilder = () => {
  // Contexto de autenticación
  const { user } = useAuth();
  
  // Estado
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [currentWidgets, setCurrentWidgets] = useState([]);
  const [userLayouts, setUserLayouts] = useState([]);
  const [currentLayout, setCurrentLayout] = useState(null);
  const [showWidgetLibrary, setShowWidgetLibrary] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedWidgetType, setSelectedWidgetType] = useState(null);
  const [editingWidget, setEditingWidget] = useState(null);
  const [layoutName, setLayoutName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [layoutToDelete, setLayoutToDelete] = useState(null);
  const [layoutToRename, setLayoutToRename] = useState(null);
  const [newLayoutName, setNewLayoutName] = useState('');
  const [gridKey, setGridKey] = useState(0); // Clave para forzar re-render de GridStack

  // Referencias
  const gridInstanceRef = useRef(null);
  const gridContainerRef = useRef(null);
  const isUpdatingFromGridStack = useRef(false); // Flag para evitar bucles
  const isResizing = useRef(false); // Flag para detectar resize activo

  // Cargar datos iniciales
  useEffect(() => {
    loadInitialData();
  }, []);

  // Inicializar GridStack cuando hay widgets
  useEffect(() => {
    if (currentWidgets.length > 0 && !gridInstanceRef.current) {
      initializeGridStack();
    }
  }, [currentWidgets.length]);

  // Sincronizar widgets con GridStack cuando cambian
  useEffect(() => {
    if (!gridInstanceRef.current || currentWidgets.length === 0) return;
    
    // NO sincronizar si el cambio viene de GridStack mismo
    if (isUpdatingFromGridStack.current) {
      isUpdatingFromGridStack.current = false;
      return;
    }
    
    // NO sincronizar si hay un resize en curso
    if (isResizing.current) {
      return;
    }
    
    // Convertir widgets HTML existentes en widgets de GridStack
    currentWidgets.forEach(widget => {
      const element = document.getElementById(widget.id);
      if (element) {
        // Si ya es parte de GridStack, actualizar su posición y tamaño
        if (element.gridstackNode) {
          gridInstanceRef.current.update(element, {
            x: widget.gridstackConfig.x,
            y: widget.gridstackConfig.y,
            w: widget.gridstackConfig.w,
            h: widget.gridstackConfig.h,
            minW: widget.gridstackConfig.minW || 2,
            minH: widget.gridstackConfig.minH || 2
          });
        } else {
          // makeWidget lee los atributos gs-* del HTML
          gridInstanceRef.current.makeWidget(element);
        }
      }
    });
  }, [currentWidgets]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (gridInstanceRef.current) {
        gridInstanceRef.current.destroy(false);
        gridInstanceRef.current = null;
      }
    };
  }, []);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      // Cargar widgets disponibles
      const widgetsResult = await dashboardService.getAvailableWidgets();
      if (widgetsResult.success && Array.isArray(widgetsResult.widgets)) {
        setAvailableWidgets(widgetsResult.widgets);
      } else {
        setAvailableWidgets([]);
      }

      // Cargar layouts del usuario
      const layoutsResult = await dashboardService.getUserLayouts();
      if (layoutsResult.success && Array.isArray(layoutsResult.layouts)) {
        setUserLayouts(layoutsResult.layouts);
      } else {
        setUserLayouts([]);
      }

      // Cargar layout por defecto
      const defaultLayoutResult = await dashboardService.getDefaultLayout();
      if (defaultLayoutResult.success && defaultLayoutResult.layout) {
        loadLayout(defaultLayoutResult.layout);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Error al cargar los datos iniciales');
    } finally {
      setIsLoading(false);
    }
  };

  const initializeGridStack = () => {
    if (gridInstanceRef.current) {
      return;
    }

    gridInstanceRef.current = GridStack.init({
      column: 12,
      cellHeight: 80,
      margin: 10,
      float: true,
      animate: true,
      resizable: {
        handles: 'e, se, s, sw, w'
      },
      draggable: {
        handle: '.widget-drag-handle'
      }
    }, gridContainerRef.current);

    // Eventos de GridStack
    gridInstanceRef.current.on('change', (event, items) => {
      handleGridChange(items);
    });
    
    // Detectar cuando empieza el resize
    gridInstanceRef.current.on('resizestart', (event, element) => {
      isResizing.current = true;
    });
    
    // Detectar cuando termina el resize
    gridInstanceRef.current.on('resizestop', (event, element) => {
      isResizing.current = false;
    });
  };

  const handleGridChange = (items) => {
    if (!items || items.length === 0) return;
    
    // Marcar que el cambio viene de GridStack
    isUpdatingFromGridStack.current = true;
    
    setCurrentWidgets(prevWidgets => {
      const updatedWidgets = prevWidgets.map(widget => {
        // GridStack items tienen el elemento en item.el, y el ID en item.el.id
        const gridItem = items.find(item => item.el?.id === widget.id);
        if (gridItem) {
          return {
            ...widget,
            gridstackConfig: {
              x: gridItem.x,
              y: gridItem.y,
              w: gridItem.w,
              h: gridItem.h,
              minW: widget.gridstackConfig.minW,
              minH: widget.gridstackConfig.minH
            }
          };
        }
        return widget;
      });
      return updatedWidgets;
    });
  };

  const loadLayout = (layout) => {
    // Destruir completamente GridStack antes de cargar nuevo layout
    if (gridInstanceRef.current) {
      gridInstanceRef.current.destroy(false);
      gridInstanceRef.current = null;
    }
    
    // Limpiar widgets primero para forzar un re-render limpio
    setCurrentWidgets([]);
    
    setCurrentLayout(layout);
    setLayoutName(layout.name);
    
    // Transformar del formato backend al formato frontend
    let widgets = [];
    
    // Parsear config si es string
    let configObj = layout.config;
    if (typeof layout.config === 'string') {
      try {
        configObj = JSON.parse(layout.config);
      } catch (error) {
        console.error('Error parseando config:', error);
        configObj = null;
      }
    }
    
    if (configObj && configObj.widgets && Array.isArray(configObj.widgets)) {
      widgets = configObj.widgets.map(widget => ({
        id: widget.id,
        type: widget.type,
        title: widget.title,
        config: widget.config || {},
        gridstackConfig: {
          x: widget.x || 0,
          y: widget.y || 0,
          w: widget.w || 4,
          h: widget.h || 3,
          minW: widget.minW || 2,
          minH: widget.minH || 2
        }
      }));
    } else if (layout.widgets && Array.isArray(layout.widgets)) {
      // Fallback para formato antiguo
      widgets = layout.widgets;
    }
    
    // Forzar re-render completo del grid cambiando la clave
    setGridKey(prev => prev + 1);
    
    // Actualizar widgets después de un breve delay para asegurar que el DOM se limpie
    setTimeout(() => {
      setCurrentWidgets(widgets);
    }, 50);
  };

  const handleAddWidget = (widgetType) => {
    // Abrir modal de configuración
    setSelectedWidgetType(widgetType);
    setEditingWidget(null);
    setShowWidgetLibrary(false);
    setShowConfigModal(true);
  };

  const handleSaveWidgetConfig = (config) => {
    const widgetDefinition = availableWidgets.find(w => w.type === selectedWidgetType);
    if (!widgetDefinition) {
      toast.error('Widget no encontrado');
      return;
    }

    if (editingWidget) {
      // Editar widget existente
      setCurrentWidgets(prev => prev.map(w => 
        w.id === editingWidget.id 
          ? { ...w, title: config.title, config }
          : w
      ));
      toast.success('Widget actualizado');
    } else {
      // Crear nuevo widget
      const newWidget = {
        id: `widget-${Date.now()}`,
        type: widgetDefinition.type,
        title: config.title || widgetDefinition.name,
        config: config,
        gridstackConfig: {
          x: 0,
          y: 0,
          w: widgetDefinition.defaultSize?.w || 4,
          h: widgetDefinition.defaultSize?.h || 3
        }
      };

      setCurrentWidgets(prev => [...prev, newWidget]);
      toast.success(`Widget "${config.title}" agregado`);
    }

    setShowConfigModal(false);
    setSelectedWidgetType(null);
    setEditingWidget(null);
  };

  const handleEditWidget = (widget) => {
    setSelectedWidgetType(widget.type);
    setEditingWidget(widget);
    setShowConfigModal(true);
  };

  const handleRemoveWidget = (widgetId) => {
    // Primero remover del estado de React
    setCurrentWidgets(prev => prev.filter(w => w.id !== widgetId));
    
    // Luego remover de GridStack después de que React actualice
    setTimeout(() => {
      if (gridInstanceRef.current) {
        const element = document.getElementById(widgetId);
        if (element) {
          try {
            gridInstanceRef.current.removeWidget(element, false); // false = no trigger events
          } catch (error) {
            console.warn('GridStack widget already removed:', error);
          }
        }
      }
    }, 0);
    
    toast.info('Widget eliminado');
  };

  const handleSaveLayout = async () => {
    // Si ya existe un currentLayout con nombre, usar ese nombre
    const finalLayoutName = layoutName.trim() || currentLayout?.name || 'Mi Dashboard';
    
    if (!finalLayoutName) {
      toast.error('Por favor ingresa un nombre para el layout');
      return;
    }

    setIsSaving(true);

    try {
      // Transformar widgets al formato que espera GridStack
      const transformedWidgets = currentWidgets.map(widget => ({
        id: widget.id,
        type: widget.type,
        title: widget.title,
        config: widget.config,
        x: widget.gridstackConfig.x,
        y: widget.gridstackConfig.y,
        w: widget.gridstackConfig.w,
        h: widget.gridstackConfig.h,
        minW: widget.gridstackConfig.minW,
        minH: widget.gridstackConfig.minH
      }));

      const layoutData = {
        name: finalLayoutName,
        config: {
          widgets: transformedWidgets,
          gridOptions: {
            column: 12,
            cellHeight: 80,
            margin: 10
          }
        },
        isDefault: false
      };

      let result;
      // Verificar si somos propietarios del layout para decidir si actualizar o crear nuevo
      const isOwner = currentLayout?.id && currentLayout?.userId && currentLayout.userId === user?.id;
      
      if (isOwner) {
        result = await dashboardService.updateLayout(currentLayout.id, layoutData);
      } else {
        result = await dashboardService.createLayout(layoutData);
      }

      if (result.success) {
        toast.success('Layout guardado correctamente');
        setShowSaveModal(false);
        
        // Actualizar el currentLayout con el ID del layout guardado
        // para futuras actualizaciones
        if (result.layout) {
          setCurrentLayout({
            id: result.layout.id,
            name: result.layout.name,
            description: result.layout.description,
            userId: result.layout.userId ?? user?.id
          });
        }
        
        // Recargar solo la lista de layouts (no recargar el dashboard actual)
        const layoutsResult = await dashboardService.getUserLayouts();
        if (layoutsResult.success && Array.isArray(layoutsResult.layouts)) {
          setUserLayouts(layoutsResult.layouts);
        }
      } else {
        toast.error(result.message || 'Error al guardar el layout');
      }
    } catch (error) {
      console.error('Error al guardar layout:', error);
      toast.error('Error al guardar el layout');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteLayout = async () => {
    if (!currentLayout?.id) {
      toast.warning('No hay layout seleccionado para eliminar');
      return;
    }

    if (!window.confirm('¿Estás seguro de eliminar este layout?')) {
      return;
    }

    try {
      const result = await dashboardService.deleteLayout(currentLayout.id);
      
      if (result.success) {
        toast.success('Layout eliminado');
        setCurrentLayout(null);
        setCurrentWidgets([]);
        setLayoutName('');
        loadInitialData();
      } else {
        toast.error(result.message || 'Error al eliminar el layout');
      }
    } catch (error) {
      console.error('Error al eliminar layout:', error);
      toast.error('Error al eliminar el layout');
    }
  };

  if (isLoading) {
    return (
      <Container fluid className="mt-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3 text-muted">Cargando dashboard...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      {/* Header */}
      <Card className="mb-3 shadow-sm">
        <Card.Body>
          <Flex justifyContent="between" alignItems="center" wrap="wrap">
            <div>
              <h4 className="mb-1">
                <FontAwesomeIcon icon={faLayerGroup} className="me-2 text-primary" />
                {currentLayout?.name || 'Nuevo Dashboard'}
              </h4>
              <p className="text-muted mb-0 small">
                Personaliza tu dashboard arrastrando y redimensionando widgets
              </p>
            </div>
            <Flex className="gap-2" wrap="wrap">
              {/* Selector de Layout */}
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
                  Layouts
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {userLayouts.length === 0 ? (
                    <Dropdown.Item disabled>No hay layouts guardados</Dropdown.Item>
                  ) : (
                    userLayouts.map(layout => {
                      const isOwner = layout.userId === user?.id;
                      return (
                        <div key={layout.id} className="dropdown-item-wrapper" style={{ display: 'flex', alignItems: 'center', padding: '0.25rem 0.5rem' }}>
                          <div 
                            onClick={() => loadLayout(layout)}
                            style={{ 
                              flex: 1, 
                              cursor: 'pointer', 
                              padding: '0.25rem 0.5rem',
                              backgroundColor: currentLayout?.id === layout.id ? 'var(--bs-primary)' : 'transparent',
                              color: currentLayout?.id === layout.id ? '#fff' : 'inherit',
                              borderRadius: '4px',
                              fontWeight: currentLayout?.id === layout.id ? '600' : 'normal',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <span style={{ color: currentLayout?.id === layout.id ? '#fff' : 'inherit' }}>
                              {layout.name}
                            </span>
                          </div>
                          {isOwner && (
                            <div className="d-flex gap-2 ms-2">
                              <Button
                                variant="link"
                                size="sm"
                                className="text-primary p-0"
                                style={{ minWidth: 'auto' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setLayoutToRename(layout);
                                  setNewLayoutName(layout.name);
                                  setShowRenameModal(true);
                                }}
                                title="Editar nombre"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </Button>
                              <Button
                                variant="link"
                                size="sm"
                                className="text-danger p-0"
                                style={{ minWidth: 'auto' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setLayoutToDelete(layout);
                                  setShowDeleteModal(true);
                                }}
                                title="Eliminar"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => {
                    setCurrentLayout(null);
                    setCurrentWidgets([]);
                    setLayoutName('Nuevo Layout');
                  }}>
                    + Crear nuevo layout
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              {/* Botones de acción */}
              <Button 
                variant="primary" 
                size="sm"
                onClick={() => setShowWidgetLibrary(true)}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Agregar Widget
              </Button>

              <Button 
                variant="success" 
                size="sm"
                onClick={() => {
                  // Si ya tiene nombre, guardar directamente sin modal
                  if (currentLayout?.name && layoutName) {
                    handleSaveLayout();
                  } else {
                    setShowSaveModal(true);
                  }
                }}
                disabled={currentWidgets.length === 0 || isSaving}
              >
                {isSaving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Guardando...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Guardar
                  </>
                )}
              </Button>

              {/* Solo mostrar botón eliminar si somos propietarios */}
              {currentLayout?.id && currentLayout?.userId === user?.id && (
                <Button 
                  variant="danger" 
                  size="sm"
                  onClick={handleDeleteLayout}
                >
                  <FontAwesomeIcon icon={faTrash} className="me-2" />
                  Eliminar
                </Button>
              )}
            </Flex>
          </Flex>
        </Card.Body>
      </Card>

      {/* Grid de Widgets */}
      {currentWidgets.length === 0 ? (
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5">
            <div className="mb-3">
              <FontAwesomeIcon icon={faLayerGroup} size="3x" className="text-muted" />
            </div>
            <h5 className="text-muted">Tu dashboard está vacío</h5>
            <p className="text-muted mb-4">
              Comienza agregando widgets desde la librería
            </p>
            <Button 
              variant="primary"
              onClick={() => setShowWidgetLibrary(true)}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Agregar tu primer widget
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div 
          key={gridKey}
          ref={gridContainerRef}
          className="grid-stack"
        >
          {currentWidgets.map(widget => (
            <div
              key={widget.id}
              id={widget.id}
              className="grid-stack-item"
              gs-x={widget.gridstackConfig.x}
              gs-y={widget.gridstackConfig.y}
              gs-w={widget.gridstackConfig.w}
              gs-h={widget.gridstackConfig.h}
              gs-min-w={widget.gridstackConfig.minW || 2}
              gs-min-h={widget.gridstackConfig.minH || 2}
            >
              <div className="grid-stack-item-content">
                <Card className="h-100 widget-card shadow-sm">
                  <Card.Header className="bg-light border-0">
                    <Flex justifyContent="between" alignItems="center">
                      <Flex alignItems="center" className="flex-grow-1">
                        <FontAwesomeIcon 
                          icon={faGripVertical} 
                          className="widget-drag-handle text-muted me-2"
                          style={{ cursor: 'move' }}
                        />
                        <h6 className="mb-0">{widget.title}</h6>
                      </Flex>
                      <Dropdown align="end">
                        <Dropdown.Toggle 
                          variant="link" 
                          size="sm"
                          className="text-muted p-0 border-0"
                          style={{ boxShadow: 'none' }}
                        >
                          <FontAwesomeIcon icon={faEllipsisV} />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => handleEditWidget(widget)}>
                            <FontAwesomeIcon icon={faEdit} className="me-2" />
                            Configurar
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item onClick={() => handleRemoveWidget(widget.id)} className="text-danger">
                            <FontAwesomeIcon icon={faTrash} className="me-2" />
                            Eliminar
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </Flex>
                  </Card.Header>
                  <Card.Body className="overflow-auto">
                    <WidgetRenderer widget={widget} />
                  </Card.Body>
                </Card>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: Librería de Widgets */}
      <WidgetLibrary
        show={showWidgetLibrary}
        onHide={() => setShowWidgetLibrary(false)}
        availableWidgets={availableWidgets}
        onAddWidget={handleAddWidget}
      />

      {/* Modal: Configurar Widget */}
      {showConfigModal && selectedWidgetType && (
        <WidgetConfigModal
          show={showConfigModal}
          onHide={() => {
            setShowConfigModal(false);
            setSelectedWidgetType(null);
            setEditingWidget(null);
          }}
          widgetType={selectedWidgetType}
          widgetDefinition={availableWidgets.find(w => w.type === selectedWidgetType)}
          existingConfig={editingWidget?.config}
          onSave={handleSaveWidgetConfig}
        />
      )}

      {/* Modal: Guardar Layout */}
      <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faSave} className="me-2" />
            Guardar Layout
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Nombre del Layout</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ej: Mi Dashboard Principal"
                value={layoutName}
                onChange={(e) => setLayoutName(e.target.value)}
                autoFocus
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveModal(false)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveLayout} disabled={isSaving}>
            {isSaving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Guardando...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Guardar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: Eliminar Layout */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Confirmar Eliminación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            ¿Estás seguro de eliminar el layout <strong>"{layoutToDelete?.name}"</strong>?
          </p>
          <p className="text-muted small mb-0 mt-2">Esta acción no se puede deshacer.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button 
            variant="danger" 
            onClick={async () => {
              if (!layoutToDelete) return;
              try {
                const result = await dashboardService.deleteLayout(layoutToDelete.id);
                if (result.success) {
                  toast.success('Layout eliminado correctamente');
                  if (currentLayout?.id === layoutToDelete.id) {
                    setCurrentLayout(null);
                    setCurrentWidgets([]);
                  }
                  setShowDeleteModal(false);
                  loadInitialData();
                } else {
                  toast.error(result.message || 'Error al eliminar');
                }
              } catch (err) {
                console.error('Error al eliminar layout:', err);
                toast.error('Error al eliminar layout');
              }
            }}
          >
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Eliminar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: Renombrar Layout */}
      <Modal show={showRenameModal} onHide={() => setShowRenameModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Renombrar Layout
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Nuevo nombre</Form.Label>
              <Form.Control
                type="text"
                value={newLayoutName}
                onChange={(e) => setNewLayoutName(e.target.value)}
                autoFocus
                placeholder="Nombre del layout"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRenameModal(false)}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={async () => {
              if (!layoutToRename || !newLayoutName.trim()) return;
              if (newLayoutName.trim() === layoutToRename.name) {
                setShowRenameModal(false);
                return;
              }
              try {
                const result = await dashboardService.updateLayout(layoutToRename.id, { name: newLayoutName.trim() });
                if (result.success) {
                  toast.success('Nombre actualizado correctamente');
                  setShowRenameModal(false);
                  loadInitialData();
                } else {
                  toast.error(result.message || 'Error al actualizar nombre');
                }
              } catch (err) {
                console.error('Error:', err);
                toast.error('Error al actualizar nombre');
              }
            }}
          >
            <FontAwesomeIcon icon={faSave} className="me-2" />
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DashboardBuilder;
